import {
	APP_SETTINGS_KEY,
	APP_THEME_CHANGED_EVENT,
	type AppTheme,
	isNativeWebView,
	resolveAppTheme,
} from '@polygraf/shared'
import { useSyncExternalStore } from 'react'

const DARK_QUERY = '(prefers-color-scheme: dark)'

/** The `data-theme` the stored settings and the device currently resolve to. */
export function currentAppTheme(): AppTheme {
	let raw: string | null = null
	try {
		raw = localStorage.getItem(APP_SETTINGS_KEY)
	} catch {
		// Storage blocked (privacy mode) — behaves like unset.
	}
	const prefersDark =
		typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches
	// Unset follows the system only inside the apps' WebView: nothing there ever
	// writes `polygraf_app_settings`, so light would be permanent on a dark phone.
	return resolveAppTheme(raw, prefersDark, isNativeWebView())
}

/**
 * Put the resolved theme on <html> and keep it current when the device scheme
 * flips. Returns the cleanup. Re-resolves from storage on every change, so a
 * reader on explicit light/dark is unaffected by the device.
 */
export function startAppThemeSync(): () => void {
	const apply = () => document.documentElement.setAttribute('data-theme', currentAppTheme())
	apply()
	// The native app rewrites the stored theme and fires this when its own
	// light/dark choice changes while the viewer is open.
	window.addEventListener(APP_THEME_CHANGED_EVENT, apply)
	if (typeof window.matchMedia !== 'function')
		return () => window.removeEventListener(APP_THEME_CHANGED_EVENT, apply)
	const media = window.matchMedia(DARK_QUERY)
	media.addEventListener('change', apply)
	return () => {
		media.removeEventListener('change', apply)
		window.removeEventListener(APP_THEME_CHANGED_EVENT, apply)
	}
}

function subscribeToTheme(onChange: () => void): () => void {
	const observer = new MutationObserver(onChange)
	observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
	return () => observer.disconnect()
}

function readTheme(): AppTheme {
	return document.documentElement.getAttribute('data-theme') === 'polygraf-dark'
		? 'polygraf-dark'
		: 'polygraf'
}

/** The theme on <html>, re-rendering when Settings or the device flips it. */
export function useAppTheme(): AppTheme {
	return useSyncExternalStore(subscribeToTheme, readTheme, () => 'polygraf')
}
